import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { companyMenuApiClient } from '@src/api/company/menu/companyMenuApiClient.ts';
import { managementMenuApiClient, MenuItem } from '@src/api/company/menu/managementMenuApiClient';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { useCategory } from '@src/context/CategoryContext';
import { useDxDiagnosisMenu } from '@src/context/DxDiagnosisMenuContext';
import { useEventMenu } from '@src/context/EventMenuContext';
import Footer from '@src/ui/templates/Company/Footer/Footer';
import MobileHeader from '@src/ui/templates/Company/MobileHeader/MobileHeader';
import CompanyMenubar from '@src/ui/templates/Company/Navbar/CompanyMenubar';
import { NavItem } from '@src/ui/templates/Company/NavMenu/types';
import CompanySidebar from '@src/ui/templates/Company/Sidebar/CompanySidebar';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

type Props = {
  children: React.ReactNode;
  pageName?: string;
  showSidebar?: boolean;
};

type SettingMenuItem = {
  category: string;
  items: {
    title: string;
    link: string;
    adminOnly: boolean;
    isComingSoon: boolean;
    isLocked: boolean;
    isActive: boolean;
  }[];
};
function CompanyDefaultLayout({ children, pageName, showSidebar = true }: Props) {
  const location = useLocation();
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [showFooter, setShowFooter] = useState(false);
  const [menuItems, setMenuItems] = useState<SettingMenuItem[]>([]);
  const companyAuth = useCompanyAuth();

  const {
    menuMediaCategories,
    menuSupportServiceCategories,
    loadMediaCategories,
    loadSupportServiceCategories,
    setSelectedCategorySlug,
  } = useCategory();
  const { eventMenus, loadEventMenus } = useEventMenu();
  const { dxDiagnosisMenus, loadDxDiagnosisMenus } = useDxDiagnosisMenu();

  const sidebarClassName = useMemo(
    () => `flex flex-col lg:w-full lg:pt-[60px]
    ${showSidebar ? ' w-[calc(100%-240px)] xl:w-[calc(100%-200px)]' : ' w-full'}`,
    [showSidebar],
  );

  const updateCurrentNavItems = useCallback(() => {
    const currentPath = location.pathname;
    setNavItems((prevNavItems) =>
      prevNavItems.map((item) => ({
        ...item,
        current: item.link ? currentPath.startsWith(item.link) : false,
      })),
    );
  }, [location.pathname]);

  const fetchCompanyMenu = useCallback(async () => {
    const res = await companyMenuApiClient.index();
    handleApiResponse(
      res,
      (data) => {
        const navItems = data.map((menu) => ({
          title: menu.title,
          link: menu.link ?? '/',
          adminOnly: menu.adminOnly,
          isComingSoon: menu.isComingSoon,
          isLocked: menu.isLocked,
          current: menu.link ? location.pathname.startsWith(menu.link) : false,
        }));
        setNavItems(navItems);
        setShowFooter(true);
      },
      (error) => {
        console.error('メニューの取得に失敗しました:', error);
      },
    );
  }, [location.pathname]);

  const fetchManagementMenu = useCallback(async () => {
    const res = await managementMenuApiClient.index();
    handleApiResponse(res, (data) => {
      const menuItems = (data || [])
        .filter((menu: MenuItem) => menu.items.length > 0)
        .map((menu: MenuItem) => ({
          category: menu.title,
          items: menu.items.map((item) => ({
            title: item.title,
            link: item.link || '/',
            adminOnly: item.adminOnly,
            isComingSoon: item.isComingSoon,
            isLocked: item.isLocked,
            isActive: location.pathname === item.link,
          })),
        }));
      setMenuItems(menuItems);
    });
  }, [location.pathname]);

  useEffect(() => {
    if (pageName === 'users' || pageName === 'companies' || pageName === 'profile' || pageName === 'bookmarks') {
      fetchManagementMenu();
    }
    fetchCompanyMenu();
  }, [pageName]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const categorySlug = urlParams.get('category_slug');
    setSelectedCategorySlug(categorySlug || undefined);
  }, [location.search, setSelectedCategorySlug]);

  useEffect(() => {
    if (pageName === 'articles') {
      loadMediaCategories();
    } else if (pageName === 'support-services') {
      loadSupportServiceCategories();
    }
  }, [pageName, loadMediaCategories, loadSupportServiceCategories]);

  const categories = useMemo(() => {
    if (pageName === 'articles') {
      return menuMediaCategories;
    } else if (pageName === 'support-services') {
      return menuSupportServiceCategories;
    }
    return [];
  }, [pageName, menuMediaCategories, menuSupportServiceCategories]);

  useEffect(() => {
    updateCurrentNavItems();
  }, [updateCurrentNavItems]);

  useEffect(() => {
    if (pageName === 'events') {
      loadEventMenus();
    }
    if (pageName === 'dx-diagnosis') {
      loadDxDiagnosisMenus();
    }
  }, [pageName, loadEventMenus, loadDxDiagnosisMenus]);

  return (
    <div className='flex min-h-screen flex-col'>
      <div className='flex grow lg:block'>
        {/* PCサイドバー */}
        {showSidebar && (
          <div className='sticky top-0 h-screen overflow-hidden lg:hidden'>
            <div className='h-screen w-[240px] shrink-0 overflow-y-auto xl:w-[200px]'>
              <CompanySidebar
                eventMenu={eventMenus}
                dxDiagnosisMenu={dxDiagnosisMenus}
                categories={categories}
                top_page_link={companyAuth.profile?.topPageLink || ''}
                pageName={pageName}
                settingMenuItems={menuItems}
              />
            </div>
          </div>
        )}
        {navItems.length > 0 && (
          <>
            {/* モバイルヘッダー */}
            <div className='lg:block'>
              <MobileHeader navItems={navItems} />
            </div>
            <div className={sidebarClassName}>
              <CompanyMenubar navItems={navItems} />
              <div className='grow'>{children}</div>
            </div>
          </>
        )}
      </div>
      {showFooter && <Footer />}
    </div>
  );
}

export default CompanyDefaultLayout;
